import { Code, ConnectError } from "@connectrpc/connect";

const COPY: Partial<Record<Code, string>> = {
  [Code.Canceled]: "The request was cancelled.",
  [Code.Unknown]: "Something went wrong. Please try again.",
  [Code.InvalidArgument]: "That input is not valid.",
  [Code.DeadlineExceeded]: "The server took too long to respond.",
  [Code.NotFound]: "We could not find that.",
  [Code.AlreadyExists]: "That already exists.",
  [Code.PermissionDenied]: "You do not have permission to do that.",
  [Code.ResourceExhausted]: "Slow down: too many requests.",
  [Code.FailedPrecondition]: "That cannot be done right now.",
  [Code.Aborted]: "The request was interrupted. Please try again.",
  [Code.OutOfRange]: "That is out of range.",
  [Code.Unimplemented]: "That is not available yet.",
  [Code.Internal]: "Something went wrong on our side.",
  [Code.Unavailable]: "TANK is unreachable right now. Check your connection.",
  [Code.DataLoss]: "Something went wrong on our side.",
  [Code.Unauthenticated]: "Please sign in again.",
};

/** Short human copy for an error. Never echoes raw vendor/transport messages. */
export function describeError(err: unknown, fallback = "Something went wrong. Please try again."): string {
  if (err instanceof ConnectError) {
    // InvalidArgument / AlreadyExists / FailedPrecondition carry validation copy the api wrote for humans.
    if (
      (err.code === Code.InvalidArgument ||
        err.code === Code.AlreadyExists ||
        err.code === Code.FailedPrecondition ||
        err.code === Code.PermissionDenied) &&
      err.rawMessage &&
      err.rawMessage.length < 140
    ) {
      return capitalize(err.rawMessage);
    }
    return COPY[err.code] ?? fallback;
  }
  return fallback;
}

export function isPermissionDenied(err: unknown): boolean {
  return err instanceof ConnectError && err.code === Code.PermissionDenied;
}

export function isUnauthenticated(err: unknown): boolean {
  return err instanceof ConnectError && err.code === Code.Unauthenticated;
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * Copy for a message that did not send. The SDK keeps only the error's text ("[permission_denied]
 * join the channel first"), so it is read back into a ConnectError and described like any other:
 * a person told only "failed" cannot tell a refusal from a dropped connection.
 */
export function describeSendError(error: string | undefined): string {
  const m = error?.match(/^\[([a-z_]+)\]\s*(.*)$/s);
  if (!m) return "Failed to send.";
  const key = m[1]!.replace(/(^|_)([a-z])/g, (_, _u: string, c: string) =>
    c.toUpperCase(),
  ) as keyof typeof Code;
  const code = Code[key];
  if (typeof code !== "number") return "Failed to send.";
  const why = describeError(new ConnectError(m[2] ?? "", code), "");
  return why ? `Failed to send: ${why.replace(/\.$/, "")}.` : "Failed to send.";
}
